
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Conversation } from "@/components/chat/types/conversation";

interface UseConversationsProps {
  currentThreadId: string | null;
}

export const useConversations = ({ currentThreadId }: UseConversationsProps) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Add a force refresh reference to ensure we can clear cache when needed
  const forceRefreshRef = useRef(false);

  useEffect(() => {
    console.log("Fetching conversations, current threadId:", currentThreadId);
    fetchConversations();
    
    // Set up a refresh timer
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchConversations(true);
      }
    }, 10000); // Check for updates every 10 seconds when tab is visible
    
    return () => clearInterval(intervalId);
  }, [currentThreadId]);

  const formatDateString = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day}.${month}.${year} kl. ${hours}:${minutes}`;
  };

  const fetchConversations = async (forceRefresh = false) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log("Fetching conversations from database...");
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error("Error getting user:", userError);
        setError("Feil ved henting av brukerinformasjon");
        setIsLoading(false);
        return;
      }
      
      if (!user) {
        console.log("No user found, cannot fetch conversations");
        setError("Ingen bruker er pålogget");
        setIsLoading(false);
        return;
      }

      console.log("User found, fetching conversations for user:", user.id);

      // Fetch conversations from database with explicit error handling
      const { data, error } = await supabase
        .from('chat_conversations')
        .select('id, thread_id, created_at, updated_at, title, last_message')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
        
      if (error) {
        console.error("Database query error:", error);
        console.error("Error details:", JSON.stringify(error, null, 2));
        
        // More specific error handling
        if (error.code === 'PGRST116') {
          setError("RLS policy blokkerer tilgang til samtaler");
        } else if (error.code === '42501') {
          setError("Utilstrekkelige tillatelser for å hente samtaler");
        } else {
          setError(`Database feil: ${error.message}`);
        }
        setIsLoading(false);
        return;
      }
      
      console.log("Fetched conversations from database:", data?.length || 0, "conversations");
      console.log("Raw conversation data:", data);
      
      if (!data || data.length === 0) {
        console.log("No conversations found in database");
        setConversations([]);
        setIsLoading(false);
        return;
      }
      
      // Format data with date
      const formattedData = data.map((conversation: any) => {
        const updatedAt = new Date(conversation.updated_at || conversation.created_at);
        const display_date = formatDateString(updatedAt);
        
        return {
          ...conversation,
          display_date
        };
      });
      
      console.log("Formatted conversations:", formattedData);
      setConversations(formattedData);
      setError(null);
    } catch (err) {
      console.error("Exception fetching conversations:", err);
      setError("Kunne ikke hente samtalehistorikken");
      
      toast({
        variant: "destructive",
        title: "Feil ved henting av samtaler",
        description: "Kunne ikke hente samtalehistorikken. Prøv å oppdatere siden.",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Retry button handler
  const handleRetryFetch = () => {
    console.log("Retrying conversation fetch...");
    fetchConversations(true); // Force refresh
    toast({
      title: "Oppdaterer samtaler",
      description: "Forsøker å laste inn samtalehistorikken på nytt.",
    });
  };

  return {
    conversations,
    isLoading,
    error,
    handleRetryFetch,
    forceRefresh: () => {
      forceRefreshRef.current = true;
      fetchConversations(true);
    }
  };
};
